import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import React from 'react';
import { render } from 'react-dom';

import {
  renderBancontactAdyenCaptureCreditCard,
  renderBancontactAdyenManageCreditCards,
} from '@entries/BancontactAdyen.entry';
import { renderPayPalCheckoutButton } from '@entries/PayPal.entry';
import PaymentAuthorizationAction, {
  PaymentAuthorizationActionProps,
} from '@features/ActionCreator/PaymentAuthorizationAction/PaymentAuthorizationAction';
import AdyenCaptureCreditCards from '@features/AdyenCreditCard/AdyenCaptureCreditCard/AdyenCaptureCreditCard';
import AdyenManageCreditCards from '@features/AdyenCreditCard/AdyenManageCreditCards/AdyenManageCreditCards';
import AdyenApplePayButtonWrapper from '@features/ApplePay/AdyenApplePayButtonWrapper/AdyenApplePayButtonWrapper';
import PaymentAuthorizationSwitch from '@features/BraintreeAuthorization/PaymentAuthorizationSwitch/PaymentAuthorizationSwitch';
import PaymentMethodSwitch, {
  PaymentMethodSwitchProps,
} from '@features/BraintreeCreditCard/PaymentMethodSwitch/PaymentMethodSwitch';
import RetryPayment from '@features/RetryPayment/RetryPayment/RetryPayment';
import { getSentryService } from '@services/sentryService';
import {
  Additional3dsInfo,
  ContainerType,
  CreditCardData,
  CreditCardType,
  CustomerType,
  FraudPreventionRecommendation,
  Locale,
  MaybeNullable,
  PaymentMethod,
  ShopBundle,
} from '@types-internal';
import { getDefaultConfig } from '@utils/configUtils';
import {
  normalizeAdyenApplePayButtonWrapperProps,
  normalizeAdyenCaptureCreditCardsProps,
  normalizeAdyenManageCreditCardsProps,
} from '@utils/extractFromHtml';

const runningEnv = window.environment;
const defaultConfig = getDefaultConfig();
const isLocalEnv = runningEnv === 'local';

function log(...values: unknown[]) {
  if (isLocalEnv) {
    console.log.apply(console.log, values);
  }
}

log('Starting app with ENV', process.env);

const base64JsonToJS = <T,>(data?: string) => {
  const jsonAsString = data && data !== '' && Buffer.from(data, 'base64').toString('utf-8');
  return (jsonAsString && (JSON.parse(jsonAsString) as T)) || undefined;
};

const getContainerProps = (
  container: HTMLElement,
  myaccount: boolean,
  registration: boolean,
): PaymentMethodSwitchProps => {
  return {
    shopBundle: container.dataset.shopBundle as ShopBundle,
    domain: container.dataset.domain as string,
    locale: container.dataset.locale?.replace('_', '-') as Locale,
    // Specific below
    errorContainerId: container.dataset.errorContainerId as string,
    currency: container.dataset.currency as string,
    sid: container.dataset.sid as string,
    siteId: container.dataset.siteId ? Number(container.dataset.siteId) : undefined,
    customerId: container.dataset.customerId as string,
    containerType: (myaccount
      ? 'myaccount'
      : registration
        ? 'registration'
        : 'default') as ContainerType,
    customerType: container.dataset.customerType as CustomerType,
    // csrfToken might be undefined for a form not needing API calls (for ex on registration page)
    paymentMethod: container.dataset.paymentMethod as PaymentMethod,
    pspToken: container.dataset.pspToken as string,
    creditCards: base64JsonToJS<CreditCardData[]>(container.dataset.creditCards as string) || [],
    csrfToken: container.dataset.csrfToken,
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    env: runningEnv,
  };
};

export const renderPaymentSwitch = (force: boolean) => {
  const container = document.getElementById('payment-method-selection') as HTMLElement;

  const myaccount = container?.dataset.myaccount === 'true';
  const registration = container?.dataset.registration === 'true';

  if (container && container.dataset.paymentMethod === 'BK') {
    const props = getContainerProps(container, myaccount, registration);

    if (props.errorContainerId) {
      if (isLocalEnv) console.log('BT Render payment-method-selection with', props);

      if (force || !registration) {
        render(
          <React.StrictMode>
            <PaymentMethodSwitch {...props} />
          </React.StrictMode>,
          container,
        );
      }
    }
  } else {
    if (isLocalEnv) console.log('BT payment-method-selection container not found');
  }
};

export const renderAdyenManageCreditCards = () => {
  const container = document.getElementById('payment-method-selection');

  if (container && container.dataset.paymentMethod === 'AK') {
    const sentryService = getSentryService(runningEnv, defaultConfig);

    try {
      const props = normalizeAdyenManageCreditCardsProps(container, runningEnv);

      log('Render Adyen payment-method-selection with', props);

      render(
        <React.StrictMode>
          <AdyenManageCreditCards {...props} />
        </React.StrictMode>,
        container,
      );
    } catch (error) {
      sentryService.captureException({ error });
    }
  } else {
    log('Adyen payment-method-selection container not found');
  }
};

export const renderAdyenCaptureCreditCard = (
  maybeCustomerType: MaybeNullable<string>,
  maybeAdyenCardBrandCodes: unknown,
) => {
  const container = document.getElementById('adyen-capture-credit-cards');

  if (container) {
    const sentryService = getSentryService(runningEnv, defaultConfig);

    try {
      const props = normalizeAdyenCaptureCreditCardsProps(
        container,
        runningEnv,
        maybeCustomerType,
        maybeAdyenCardBrandCodes,
      );

      log('Render adyen-capture-credit-cards with', props);

      render(
        <React.StrictMode>
          <AdyenCaptureCreditCards {...props} />
        </React.StrictMode>,
        container,
      );
    } catch (error) {
      sentryService.captureException({ error });
    }
  } else {
    log('adyen-capture-credit-cards container not found');
  }
};

const renderAdyenApplePayButton = (
  maybePaymentMethodsResponse: unknown,
  maybeOnSubmit: unknown,
  maybeOnError: unknown,
) => {
  const container = document.getElementById('adyen-apple-pay-button');

  if (container) {
    const sentryService = getSentryService(runningEnv, defaultConfig);

    try {
      const props = normalizeAdyenApplePayButtonWrapperProps(
        container,
        runningEnv,
        maybePaymentMethodsResponse,
        maybeOnSubmit,
        maybeOnError,
      );

      log('Render adyen-apple-pay-button with', props);

      render(
        <React.StrictMode>
          <AdyenApplePayButtonWrapper {...props} />
        </React.StrictMode>,
        container,
      );
    } catch (error) {
      sentryService.captureException({ error });
    }
  } else {
    log('adyen-apple-pay-button container not found');
  }
};

const renderAuthorizer = () => {
  const container = document.getElementById('payment-authorization') as HTMLElement;

  if (container) {
    const props = {
      shopBundle: container.dataset.shopBundle as ShopBundle,
      domain: container.dataset.domain as string,
      locale: container.dataset.locale?.replace('_', '-') as Locale,
      // Specific below
      errorContainerId: container.dataset.errorContainerId as string,
      amount: (container.dataset.amount && parseFloat(container.dataset.amount)) || 0,
      currency: container.dataset.currency as string,
      paymentMethod: container.dataset.paymentMethod as PaymentMethod,
      pspToken: container.dataset.pspToken as string,
      braintreeNonceToken: container.dataset.braintreeNonceToken as string,
      braintreeBinData: base64JsonToJS(container.dataset.braintreeBinData as string) as
        | string
        | undefined,
      braintreeBin: container.dataset.braintreeBin as string,
      shouldUseSimpleBin: container.dataset.shouldUseSimpleBin === 'true',
      csrfToken: container.dataset.csrfToken,
      additional3dsInfo: base64JsonToJS(
        container.dataset['additional-3dsInfo'] as string,
      ) as Additional3dsInfo,
      cardType: container.dataset.cardType as CreditCardType,
      isSubscriptionFirst: container.dataset.subscriptionFirst === 'true',
      subscriptionFirstInterval: container.dataset.subscriptionFirstInterval as string,
      customerId: container.dataset.customerId as string,
      customerType: container.dataset.customerType as string,
      sid: container.dataset.sid as string,
      fraudPreventionRecommendation: container.dataset
        .fraudPreventionRecommendation as FraudPreventionRecommendation,
      siteId: Number(container.dataset.siteId as string),
      env: runningEnv,
    };

    if (props.errorContainerId) {
      if (isLocalEnv) console.log('BT Render payment-authorization with', props);

      render(
        <React.StrictMode>
          <PaymentAuthorizationSwitch {...props} />
        </React.StrictMode>,
        container,
      );
    }
  } else {
    if (isLocalEnv) console.log('BT payment-authorization container not found');
  }
};

const renderRetryPayment = () => {
  const container = document.getElementById('payment-retry') as HTMLElement;

  if (container) {
    const props = {
      shopBundle: container.dataset.shopBundle as ShopBundle,
      domain: container.dataset.domain as string,
      locale: container.dataset.locale?.replace('_', '-') as Locale,
      // Specific below
      orderId: container.dataset.orderId as string,
      customerId: container.dataset.customerId as string,
      env: runningEnv,
    };
    render(
      <React.StrictMode>
        <RetryPayment {...props} />
      </React.StrictMode>,
      container,
    );
  } else {
    if (isLocalEnv) console.log('Payment-retry container not found');
  }
};

const renderAuthorizationActionCreator = () => {
  const container = document.getElementById('payment-auth-action-creator-container') as HTMLElement;

  if (container) {
    const props: PaymentAuthorizationActionProps = {
      shopBundle: container.dataset.shopBundle as ShopBundle,
      domain: container.dataset.domain as string,
      locale: container.dataset.locale?.replace('_', '-') as Locale,
      paymentId: container.dataset.paymentId as string,
      paymentMethod: container.dataset.paymentMethod as PaymentMethod,
      csrfToken: container.dataset.csrfToken as string,
      paymentAction: base64JsonToJS(container.dataset.paymentAction) as PaymentAction,
      env: runningEnv,
      customerId: container.dataset.customerId,
      storeCardDetails: container.dataset.storeCardDetails === 'true',
      processPaymentDetailsUrl: container.dataset.termUrl ?? '/',
      isMobileApp: container.dataset.mobileApp === 'true',
      sid: container.dataset.sid,
      currency: container.dataset.currency,
      siteId: container.dataset.siteId ? Number(container.dataset.siteId) : undefined,
      customerType: container.dataset.customerType,
    };

    render(
      <React.StrictMode>
        <PaymentAuthorizationAction {...props} />
      </React.StrictMode>,
      container,
    );
  } else {
    if (isLocalEnv) console.log('payment-auth-action-creator container not found');
  }
};

renderPaymentSwitch(false);
renderAuthorizer(); // Braintree Authorizer
renderRetryPayment();

// Adyen Authorizer: Bancontact QR Code and credit card 3DS
renderAuthorizationActionCreator();

window.Payment = {
  ...(window.Payment || {}),
  braintreeRenderPaymentSwitch: () => renderPaymentSwitch(true),
  braintreeRenderAuthorizer: renderAuthorizer,
  renderAdyenCaptureCreditCard,
  renderBancontactAdyenCaptureCreditCard,
  renderAdyenManageCreditCards,
  renderBancontactAdyenManageCreditCards,
  renderAdyenApplePayButton,
  renderRetryPayment,
  PayPal: {
    renderCheckoutButton: renderPayPalCheckoutButton,
  },
};
